import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { WhatsappLogo, X } from "@phosphor-icons/react";
import { trackEvent } from "../lib/analytics";

// Swiss mobile number — strip non-digits for wa.me
const WHATSAPP_NUMBER = "41764684773";

export const WhatsAppFab = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [visible, setVisible] = useState(false);

  // Show only after a slight scroll, so it doesn't compete with the hero on first paint
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 240);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const message = encodeURIComponent(
    t(
      "whatsapp.message",
      "Olá, gostaria de informações sobre os tratamentos da Kosmedico."
    )
  );
  const href = `https://wa.me/${WHATSAPP_NUMBER}?text=${message}`;

  const handleToggle = () => {
    setOpen((v) => {
      const next = !v;
      trackEvent(next ? "whatsapp_open" : "whatsapp_close", {
        source: "fab",
        location: typeof window !== "undefined" ? window.location.pathname : "",
      });
      return next;
    });
  };

  const handleStart = () => {
    trackEvent("whatsapp_start_chat", {
      source: "fab_card",
      number: WHATSAPP_NUMBER,
      location: typeof window !== "undefined" ? window.location.pathname : "",
    });
  };

  return (
    <>
      {/* Tooltip / preview card */}
      {open && (
        <div
          data-testid="whatsapp-card"
          className="fixed bottom-24 right-5 lg:right-7 z-[60] w-[280px] bg-white border border-[#E4E2DA] rounded-sm shadow-2xl overflow-hidden animate-[fadeUp_0.35s_ease-out]"
        >
          <div className="bg-[#075E54] text-white px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <WhatsappLogo size={20} weight="fill" />
              <span className="font-medium text-sm">Kosmedico</span>
            </div>
            <button
              type="button"
              onClick={handleToggle}
              aria-label="close"
              className="text-white/80 hover:text-white"
            >
              <X size={16} weight="bold" />
            </button>
          </div>
          <div className="p-4">
            <div className="bg-[#F6F5F2] rounded-sm p-3 text-xs text-[#3E5239] leading-relaxed">
              {t(
                "whatsapp.preview",
                "Olá! Como posso ajudá-la a planear o seu tratamento?"
              )}
            </div>
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="whatsapp-cta"
              onClick={handleStart}
              className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1DA851] text-white text-xs uppercase tracking-[0.2em] py-3 transition-colors"
            >
              <WhatsappLogo size={16} weight="fill" />
              {t("whatsapp.start", "Iniciar conversa")}
            </a>
          </div>
        </div>
      )}

      {/* Floating button */}
      <button
        type="button"
        onClick={handleToggle}
        data-testid="whatsapp-fab"
        aria-label="WhatsApp"
        className={`fixed bottom-6 right-5 lg:right-7 z-[60] w-14 h-14 lg:w-16 lg:h-16 rounded-full bg-[#25D366] hover:bg-[#1DA851] text-white shadow-[0_10px_30px_-5px_rgba(37,211,102,0.6)] flex items-center justify-center transition-all duration-300 ${
          visible
            ? "opacity-100 translate-y-0 pointer-events-auto"
            : "opacity-0 translate-y-4 pointer-events-none"
        }`}
      >
        {/* ping ring */}
        <span className="absolute inset-0 rounded-full bg-[#25D366] opacity-60 animate-ping" />
        <WhatsappLogo size={28} weight="fill" className="relative" />
      </button>
    </>
  );
};
